(() => {
    const DEFAULT_INDEX = 'shopify_products';

    const ensureAlgoliaUtils = () => {
        if (window.poAlgoliaUtils) return window.poAlgoliaUtils;

        const getConfig = () => window.algoliaShopify?.config;
        const COUNTRY_MARKET_LANGUAGE_OVERRIDES = {
            italy: 'it',
            france: 'fr',
            germany: 'de',
            spain: 'es',
            portugal: 'pt-PT',
        };
        const COUNTRY_CODE_TO_MARKET_SUFFIX = {
            IT: 'italy_it',
            FR: 'france_fr',
            DE: 'germany_de',
            ES: 'spain_es',
            PT: 'portugal_pt-PT',
        };
        const LOCALE_COUNTRY_LANGUAGE_OVERRIDES = {
            it: 'it',
            fr: 'fr',
            de: 'de',
            es: 'es',
            pt: 'pt',
        };
        const escapeRegex = (value) => String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const getLocaleCountryCode = (value) => {
            const normalized = String(value || '').trim().toLowerCase();
            if (!normalized) return '';

            const segments = normalized.split('-').filter(Boolean);
            return segments.length > 1 ? segments[segments.length - 1] : normalized;
        };
        const getCountryCodeFromRootUrl = (value) => {
            const normalized = String(value || '').trim().toLowerCase();
            const match = normalized.match(/^\/?([a-z]{2})-([a-z]{2})(?:\/|$)/);
            return match?.[2] ? match[2].toUpperCase() : '';
        };
        const getCurrentCountryCode = () => {
            const localeCountry = window.SHOP_LOCALES?.current?.iso_code?.split?.('-')?.[1];
            const queryCountry =
                typeof window !== 'undefined'
                ? new URLSearchParams(window.location.search).get('country')
                : '';
            const candidates = [
                window.poShopifyLocalization?.country?.isoCode,
                getCountryCodeFromRootUrl(window.poShopifyLocalization?.locale?.rootUrl),
                getCountryCodeFromRootUrl(window.SHOP_LOCALES?.current?.root_url),
                getCountryCodeFromRootUrl(window.location?.pathname),
                queryCountry,
                window.Shopify?.country,
                document?.documentElement?.lang?.split?.('-')?.[1],
                localeCountry,
            ];

            for (const candidate of candidates) {
                const normalized = String(candidate || '').trim().toUpperCase();
                if (/^[A-Z]{2}$/.test(normalized)) {
                    return normalized;
                }
            }

            return '';
        };
        const getCurrentMarketId = () =>
            window.poShopifyLocalization?.market?.id
            || window.algoliaShopify?.shopify?.market?.id
            || null;
        const getCurrentMarketHandle = () =>
            String(
                window.poShopifyLocalization?.market?.handle
                || window.algoliaShopify?.shopify?.market?.handle
                || ''
            ).trim().toLowerCase();
        const getCurrentMarket = () => {
            const cfg = getConfig();
            const markets = Array.isArray(cfg?.markets_mapping) ? cfg.markets_mapping : [];
            const canonicalSuffix = COUNTRY_CODE_TO_MARKET_SUFFIX[getCurrentCountryCode()];

            if (canonicalSuffix) {
                const byCanonicalSuffix = markets.find(
                    (entry) => String(entry?.suffix || '').trim().toLowerCase() === canonicalSuffix
                );

                if (byCanonicalSuffix) {
                    return byCanonicalSuffix;
                }

                return { suffix: canonicalSuffix };
            }

            const marketId = getCurrentMarketId();

            if (marketId !== null && marketId !== undefined && String(marketId).trim() !== '') {
                const byId = markets.find((entry) => String(entry?.id) === String(marketId));
                if (byId) {
                    return byId;
                }
            }

            const marketHandle = getCurrentMarketHandle();
            if (marketHandle) {
                const byHandle = markets.find((entry) => {
                    const candidates = [
                        entry?.handle,
                        entry?.market_handle,
                        entry?.name,
                    ];

                    return candidates.some(
                        (candidate) => String(candidate || '').trim().toLowerCase() === marketHandle
                    );
                });

                if (byHandle) {
                    return byHandle;
                }
            }

            if (canonicalSuffix) {
                return { suffix: canonicalSuffix };
            }

            return null;
        };
        const getCurrentMarketSuffix = () => {
            const market = getCurrentMarket();
            return typeof market?.suffix === 'string' ? market.suffix.trim() : '';
        };
        const normalizeMarketSuffix = (suffix) => {
            const normalized = String(suffix || '').trim().toLowerCase();
            if (!normalized) return '';

            const shortMatch = normalized.match(/^([a-z]{2})_([a-z]{2}(?:-[a-z]{2})?)$/);
            if (shortMatch) {
                const [, marketCode, localeCode] = shortMatch;
                const countryCode = getLocaleCountryCode(localeCode) || marketCode;
                const forcedLanguage =
                    LOCALE_COUNTRY_LANGUAGE_OVERRIDES[countryCode]
                    || LOCALE_COUNTRY_LANGUAGE_OVERRIDES[marketCode];
                return forcedLanguage ? `${countryCode}_${forcedLanguage}` : normalized;
            }

            const wordMatch = normalized.match(/^([a-z_]+)_([a-z]{2}(?:-[a-z]{2})?)$/);
            if (wordMatch) {
                const [, countryKey, localeCode] = wordMatch;
                const forcedLanguage =
                    COUNTRY_MARKET_LANGUAGE_OVERRIDES[countryKey]
                    || LOCALE_COUNTRY_LANGUAGE_OVERRIDES[getLocaleCountryCode(localeCode)];
                return forcedLanguage ? `${countryKey}_${forcedLanguage}` : normalized;
            }

            return normalized;
        };
        const buildProductsIndexName = ({ indexPrefix, marketSuffix } = {}) => {
            if (!indexPrefix) {
                return DEFAULT_INDEX;
            }

            const normalizedSuffix = normalizeMarketSuffix(marketSuffix);
            return `${indexPrefix}products${normalizedSuffix ? `_${normalizedSuffix}` : ''}`;
        };
        const extractMarketPricingReplicaParts = (indexName, baseProductsIndexName) => {
            const normalized = String(indexName || '').trim();
            if (!normalized || !baseProductsIndexName) return null;

            const match = normalized.match(
                new RegExp(
                    `^${escapeRegex(baseProductsIndexName)}(?:_([a-z]+_[a-z]{2}(?:-[a-z]{2})?|[a-z]{2}_[a-z]{2}(?:-[a-z]{2})?))?_market_pricing_([a-z]{3})(?:\\.|_)price_(asc|desc)(?=(_|$))`,
                    'i'
                )
            );

            if (!match) {
                return null;
            }

            return {
                marketSuffix: match[1] || '',
                currency: String(match[2] || '').trim().toLowerCase(),
                direction: String(match[3] || '').trim().toLowerCase(),
            };
        };
        const buildPricingReplicaIndex = ({ currency, direction, indexPrefix, marketSuffix } = {}) => {
            const normalizedCurrency = String(currency || '').trim().toLowerCase();
            const normalizedDirection = String(direction || '').trim().toLowerCase();

            if (!/^[a-z]{3}$/.test(normalizedCurrency) || !/^(asc|desc)$/.test(normalizedDirection)) {
                return null;
            }

            const baseProductsIndexName = indexPrefix ? `${indexPrefix}products` : DEFAULT_INDEX;
            const normalizedSuffix = normalizeMarketSuffix(marketSuffix);

            return `${baseProductsIndexName}${normalizedSuffix ? `_${normalizedSuffix}` : ''}_market_pricing_${normalizedCurrency}.price_${normalizedDirection}`;
        };
        const normalizeIndexName = (indexName) => {
            if (indexName == null) return indexName;

            const normalized = window.__poNormalizeAlgoliaIndexName?.(indexName) || String(indexName).trim();
            if (!normalized) return normalized;

            const cfg = getConfig();
            const baseProductsIndexName = cfg?.index_prefix ? `${cfg.index_prefix}products` : DEFAULT_INDEX;
            const currentMarketSuffix = getCurrentMarketSuffix();
            const replicaParts = extractMarketPricingReplicaParts(normalized, baseProductsIndexName);
            const resolvedMarketSuffix =
                currentMarketSuffix
                || replicaParts?.marketSuffix
                || COUNTRY_CODE_TO_MARKET_SUFFIX[getCurrentCountryCode()];
            const normalizedMarketSuffix = normalizeMarketSuffix(
                resolvedMarketSuffix
            );

            if (replicaParts) {
                return (
                    buildPricingReplicaIndex({
                        currency: replicaParts.currency,
                        direction: replicaParts.direction,
                        indexPrefix: cfg?.index_prefix,
                        marketSuffix: resolvedMarketSuffix,
                    })
                    || normalized
                );
            }

            if (!normalizedMarketSuffix) {
                return normalized;
            }

            if (normalized.toLowerCase() === String(baseProductsIndexName).toLowerCase()) {
                return `${baseProductsIndexName}_${normalizedMarketSuffix}`;
            }

            if (!currentMarketSuffix) {
                return normalized;
            }

            if (currentMarketSuffix.toLowerCase() === normalizedMarketSuffix) {
                return normalized;
            }

            const normalizedByCurrentMarket = normalized.replace(
                new RegExp(`(products_)${escapeRegex(currentMarketSuffix)}(?=(_|$))`, 'i'),
                `$1${normalizedMarketSuffix}`
            );

            if (normalizedByCurrentMarket !== normalized) {
                return normalizedByCurrentMarket;
            }

            return normalized.replace(
                new RegExp(
                    `(${escapeRegex(baseProductsIndexName)}_)([a-z]+_[a-z]{2}(?:-[a-z]{2})?|[a-z]{2}_[a-z]{2}(?:-[a-z]{2})?)(?=(_|$))`,
                    'i'
                ),
                `$1${normalizedMarketSuffix}`
            );
        };
        const getDefaultProductsIndex = () => {
            const cfg = getConfig();
            const market = getCurrentMarket();

            return buildProductsIndexName({
                indexPrefix: cfg?.index_prefix,
                marketSuffix: market?.suffix || COUNTRY_CODE_TO_MARKET_SUFFIX[getCurrentCountryCode()],
            });
        };
        const getLastIndexName = (fallback = getDefaultProductsIndex()) => {
            const resolvedIndex =
                window.__poPlpCurrentIndexName ||
                window.algoliaPoCustomSearch?.loadMoreIndex ||
                window.algoliaShopify?.instantsearch?.search?.helper?.state?.index ||
                window.algoliaShopify?.instantsearch?.search?.helper?.lastResults?.index;

            if (resolvedIndex == null) {
                return fallback;
            }

            return normalizeIndexName(resolvedIndex);
        };
        const buildQueryUrl = ({appId, apiKey} = {}) => {
            const cfg = getConfig();
            const resolvedAppId = appId || cfg?.app_id;
            const resolvedApiKey = apiKey || cfg?.search_api_key;

            if (!resolvedAppId || !resolvedApiKey) return null;

            const url = new URL(`//${resolvedAppId}-dsn.algolia.net/1/indexes/*/queries`, window.location.origin);
            url.searchParams.set('x-algolia-api-key', resolvedApiKey);
            url.searchParams.set('x-algolia-application-id', resolvedAppId);
            return url;
        };

        window.poAlgoliaUtils = {
            DEFAULT_INDEX,
            getConfig,
            getCurrentMarket,
            normalizeMarketSuffix,
            buildProductsIndexName,
            buildPricingReplicaIndex,
            normalizeIndexName,
            getDefaultProductsIndex,
            getLastIndexName,
            buildQueryUrl
        };
        return window.poAlgoliaUtils;
    };

    const poAlgoliaUtils = ensureAlgoliaUtils();

    const patchAlgoliaFetch = () => {
        if (typeof window === 'undefined' || typeof window.fetch !== 'function' || window.fetch.__poAlgoliaPatched) {
            return;
        }

        const originalFetch = window.fetch.bind(window);
        const algoliaEndpointPattern = /\/1\/indexes\/\*\/(?:queries|recommendations)(?:\?|$)/i;
        const normalizeRequestBody = (rawBody) => {
            if (typeof rawBody !== 'string' || !rawBody.trim()) {
                return rawBody;
            }

            try {
                const parsed = JSON.parse(rawBody);
                if (!Array.isArray(parsed?.requests)) {
                    return rawBody;
                }

                let didChange = false;
                const nextRequests = parsed.requests.map((request) => {
                    if (!request || typeof request !== 'object') {
                        return request;
                    }

                    const normalizedIndexName = poAlgoliaUtils.normalizeIndexName?.(request.indexName) || request.indexName;
                    if (normalizedIndexName !== request.indexName) {
                        didChange = true;
                        return {
                            ...request,
                            indexName: normalizedIndexName,
                        };
                    }

                    return request;
                });

                if (!didChange) {
                    return rawBody;
                }

                return JSON.stringify({
                    ...parsed,
                    requests: nextRequests,
                });
            } catch (_error) {
                return rawBody;
            }
        };

        const patchedFetch = (input, init) => {
            const requestUrl = typeof input === 'string' ? input : input?.url;
            if (!requestUrl || !algoliaEndpointPattern.test(String(requestUrl))) {
                return originalFetch(input, init);
            }

            if (input instanceof Request && !init) {
                return originalFetch(input, init);
            }

            const nextInit = init && typeof init === 'object' ? { ...init } : {};
            const candidateBody = nextInit.body;
            const normalizedBody = normalizeRequestBody(candidateBody);

            if (normalizedBody !== candidateBody) {
                nextInit.body = normalizedBody;
            }

            return originalFetch(input, nextInit);
        };

        patchedFetch.__poAlgoliaPatched = true;
        window.fetch = patchedFetch;
    };

    patchAlgoliaFetch();

    const HANDLE_QUERY_CACHE_TTL_MS = 1500;

    const ensureAlgoliaCache = () => {
        if (window.poAlgoliaCache) return window.poAlgoliaCache;
        const cards = new Map();
        const handleQueries = new Map();
        window.poAlgoliaCache = {
            cards,
            handleQueries,
            clearCards: () => cards.clear(),
            clearHandleQueries: () => handleQueries.clear(),
        };
        return window.poAlgoliaCache;
    };

    ensureAlgoliaCache();

    if (window.poAlgoliaFetchCollectionHandles) return;

    const getLoggers = (logger, prefix) => {
        const label = typeof prefix === 'string' ? prefix : '[po-algolia]';
        const debugFn = (logger && logger.debug) || console.debug || console.log;
        const errorFn = (logger && logger.error) || console.error || console.log;

        return {
            debug: (...args) => debugFn.call(logger?.debug ? logger : console, label, ...args),
            error: (...args) => errorFn.call(logger?.error ? logger : console, label, ...args),
        };
    };

    const buildHandleQueryCacheKey = (url, body) =>
        JSON.stringify({
            url: String(url || ''),
            requests: Array.isArray(body?.requests)
                ? body.requests.map((request) => ({
                    indexName: String(request?.indexName || '').trim(),
                    params: String(request?.params || '').trim(),
                }))
                : [],
        });

    const pruneExpiredHandleQueries = (cache, now = Date.now()) => {
        if (!(cache instanceof Map)) return;

        for (const [key, entry] of cache.entries()) {
            const settledAt = Number(entry?.settledAt || 0);
            if (!entry || (settledAt && (now - settledAt) >= HANDLE_QUERY_CACHE_TTL_MS)) {
                cache.delete(key);
            }
        }
    };

    const normalizeRuleContexts = (ruleContexts, collectionHandle, includeCollectionHandleInRuleContexts = true) => {
        const resolvedRuleContexts = Array.isArray(ruleContexts)
            ? ruleContexts
            : [ruleContexts];

        const normalizedContexts = resolvedRuleContexts
            .map((ruleContext) => String(ruleContext || '').trim())
            .filter(Boolean);

        if (includeCollectionHandleInRuleContexts) {
            normalizedContexts.push(String(collectionHandle || '').trim());
        }

        return [...new Set(normalizedContexts.filter(Boolean))];
    };

    const buildParams = (
        limit,
        collectionHandle,
        showOnlyAvailable = false,
        ruleContexts,
        includeCollectionHandleInRuleContexts = true
    ) => {
        const params = new URLSearchParams();
        const filters = [];
        const normalizedRuleContexts = normalizeRuleContexts(
            ruleContexts,
            collectionHandle,
            includeCollectionHandleInRuleContexts
        );

        if (collectionHandle) {
            filters.push(`collections:${collectionHandle}`);
        }

        if (showOnlyAvailable) {
            filters.push('inventory_available:true');
        }

        params.set('hitsPerPage', String(limit));
        params.set('clickAnalytics', 'true');
        params.set('distinct', 'true');
        if (filters.length) {
            params.set('filters', filters.join(' AND '));
        }
        const userToken = window.poAlgoliaInsights?.getUserToken?.();
        if (userToken) {
            params.set('userToken', userToken);
        }
        params.set(
            'ruleContexts',
            JSON.stringify(
                normalizedRuleContexts.includes('POPULAR_ITEMS')
                    ? [...new Set([...normalizedRuleContexts, 'upc_collapsed'])]
                    : normalizedRuleContexts
            )
        );
        return params.toString();
    };

    window.poAlgoliaFetchCollectionHandles = async function ({
                                                                 collectionHandle,
                                                                 limit = 1,
                                                                 indexName,
                                                                 logPrefix,
                                                                 showOnlyAvailable = false,
                                                                 ruleContext,
                                                                 ruleContexts,
                                                                 includeCollectionHandleInRuleContexts = true,
                                                                 debugLabel = 'Algolia handles:',
                                                                 errorLabel = 'fetchCollectionHandles error',
                                                                 logger,
                                                                 includeMetadata = false,
                                                             } = {}) {
        const {debug, error} = getLoggers(logger, logPrefix);
        const url = poAlgoliaUtils.buildQueryUrl();
        const safeLimit = Math.max(1, Number(limit) || 1);

        if (!url) {
            error('Algolia config mancante');
            return [];
        }

        const chosenIndex =
            indexName ||
            poAlgoliaUtils.getLastIndexName(null) ||
            poAlgoliaUtils.getDefaultProductsIndex();

        const body = {
            requests: [{
                indexName: chosenIndex,
                params: buildParams(
                    safeLimit,
                    collectionHandle,
                    showOnlyAvailable,
                    ruleContexts || ruleContext,
                    includeCollectionHandleInRuleContexts
                ),
            },],
        };
        const cache = ensureAlgoliaCache()?.handleQueries;
        const cacheKey = buildHandleQueryCacheKey(url, body);

        try {
            const now = Date.now();
            pruneExpiredHandleQueries(cache, now);

            let cachedEntry = cache?.get(cacheKey);
            if (!cachedEntry && cache) {
                const promise = (async () => {
                    const res = await fetch(url.toString(), {
                        method: 'POST', body: JSON.stringify(body),
                    });

                    if (!res.ok) {
                        throw new Error(`Algolia HTTP error ${res.status}`);
                    }

                    const result = (await res.json())?.results?.[0] || {};
                    const hits = Array.isArray(result?.hits) ? result.hits : [];
                    const queryID = String(result?.queryID || '').trim();
                    const resolvedIndexName =
                        poAlgoliaUtils.normalizeIndexName?.(result?.index || result?.indexName || chosenIndex)
                        || chosenIndex;

                    const seen = new Set();
                    const items = [];

                    for (const hit of hits) {
                        const handle = hit?.handle;
                        if (handle && !seen.has(handle)) {
                            seen.add(handle);
                            items.push({
                                handle,
                                objectID: String(hit?.objectID || '').trim(),
                                queryID,
                                indexName: resolvedIndexName,
                                position: items.length + 1,
                                productId: String(hit?.id || '').trim(),
                                title: String(hit?.title || '').trim(),
                                price: hit?.price ?? null,
                            });
                            if (items.length >= safeLimit) break;
                        }
                    }

                    return items;
                })();

                cachedEntry = {
                    createdAt: now,
                    settledAt: 0,
                    promise,
                };
                cache.set(cacheKey, cachedEntry);

                promise
                    .then((items) => {
                        if (!cache?.has(cacheKey)) return;
                        cache.set(cacheKey, {
                            createdAt: now,
                            settledAt: Date.now(),
                            promise: Promise.resolve(items),
                        });
                    })
                    .catch(() => {
                        cache?.delete(cacheKey);
                    });
            }

            const items = await (cachedEntry?.promise || Promise.resolve([]));

            debug(debugLabel, includeMetadata ? items : items.map((item) => item.handle));
            return includeMetadata ? items : items.map((item) => item.handle);
        } catch (err) {
            cache?.delete(cacheKey);
            error(errorLabel, err);
            return [];
        }
    };
})();
